/**
 * Zustand store for account data (positions, orders, balance).
 * 
 * Platform-agnostic - works in both web and React Native.
 * 
 * Implements industry-standard exchange pattern:
 * - HTTP for initial load and mutations (source of truth for actions)
 * - WebSocket for real-time delta updates (merged only if newer)
 * 
 * Key principles:
 * 1. All data has timestamps for ordering
 * 2. HTTP mutation responses are applied immediately
 * 3. WebSocket updates are merged only if timestamp is newer
 * 4. Single source of truth prevents race conditions
 * 
 * Usage:
 * - Select raw data with selectors: useAccountStore((state) => state.positions)
 * - Use useMemo in components to derive filtered lists
 * - Use useAccountStore.getState() for imperative access in effects/callbacks
 */

import { create } from "zustand";
import type { Order, Position, AccountResponse } from "../types";
import { parseDecimal } from "../types";
import { usePriceStore } from "./price-store";

// Frontend-friendly position type
export interface StorePosition {
  id: string;
  market: string;
  side: "long" | "short";
  size: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  liquidationPrice: number;
  unrealizedPnl: number;
  realizedPnl: number;
  margin: number;
  status: "open" | "closed";
  openedAt: Date;
  updatedAt: Date;
  takeProfit?: number;
  stopLoss?: number;
  timestamp: number; // For ordering updates
}

// Frontend-friendly order type
export interface StoreOrder {
  id: string;
  market: string;
  side: "buy" | "sell";
  orderType: "market" | "limit";
  price: number;
  size: number;
  filledSize: number;
  status: string;
  takeProfit: number | null;
  stopLoss: number | null;
  createdAt: Date;
  updatedAt: Date;
  timestamp: number; // For ordering updates
}

// Frontend-friendly balance type
export interface StoreBalance {
  collateral: number;
  availableMargin: number;
  usedMargin: number;
  unrealizedPnl: number;
  realizedPnl: number;
  totalPositionValue: number;
  accountLeverage: number;
  marginRatio: number;
  marginMode: "isolated" | "cross";
  timestamp: number; // For ordering updates
}

// Frontend-friendly fill type (for trade history)
export interface StoreFill {
  id: string;
  orderId: string;
  market: string;
  side: "buy" | "sell";
  size: number;
  price: number;
  fee: number;
  realizedPnl: number;
  fillType: string;
  createdAt: Date;
  timestamp: number; // For ordering updates
}

// Frontend-friendly funding payment type
export interface StoreFundingPayment {
  id: string;
  market: string;
  positionSize: number;
  fundingRate: number;
  payment: number;
  createdAt: Date;
  timestamp: number; // For ordering updates
}

// Tombstone expiry time (30 seconds) - after this, we accept WS updates again
// This is long enough for any close order to fully process on the backend
const TOMBSTONE_EXPIRY_MS = 30000;

// PERFORMANCE: Memory leak prevention - limit historical data
// Industry standard: Keep recent history, not all history forever
const MAX_FILLS = 500;           // Last 500 trade fills (plenty for UI display)
const MAX_FUNDING_PAYMENTS = 200; // Last 200 funding payments (~2-3 months)

// Store state
interface AccountState {
  // Data stores with timestamps
  positions: Map<string, StorePosition>;
  orders: Map<string, StoreOrder>;
  balance: StoreBalance | null;
  fills: Map<string, StoreFill>;
  fundingPayments: Map<string, StoreFundingPayment>;
  
  // Tombstones for optimistically deleted positions
  // Maps market -> deletion timestamp
  // Prevents stale WebSocket updates from re-adding closed positions
  deletedPositions: Map<string, number>;
  
  // Sync timestamps
  lastHttpSync: number;
  lastWsUpdate: number;
  
  // Loading states
  isInitialized: boolean;
  isLoading: boolean;
  
  // Actions
  syncFromHttp: (data: HttpSyncData) => void;
  applyMutationResult: (result: MutationResult) => void;
  applyWsPositionUpdate: (position: WsPositionData) => void;
  applyWsOrderUpdate: (order: WsOrderData) => void;
  applyWsBalanceUpdate: (balance: WsBalanceData) => void;
  applyWsFillUpdate: (fill: WsFillData) => void;
  applyWsFundingUpdate: (payment: WsFundingData) => void;
  updatePositionFromHttp: (position: Position) => void; // Immediate position update from HTTP
  removePosition: (market: string) => void; // Optimistic removal for close
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

// HTTP sync payload
interface HttpSyncData {
  account?: AccountResponse;
  positions?: Position[];
  orders?: Order[];
}

// Mutation result payload
interface MutationResult {
  order?: Order;
  position?: Position;
  balance?: {
    collateral: string;
    available_margin: string;
    used_margin: string;
  };
}

// WebSocket position update
interface WsPositionData {
  id: string;
  user_id: string;
  market: string;
  side: string;
  size: string;
  entry_price: string;
  mark_price: string;
  leverage: string;
  margin: string;
  unrealized_pnl: string;
  realized_pnl: string;
  liquidation_price: string | null;
  opened_at: string;
  take_profit: string | null;
  stop_loss: string | null;
  timestamp: number;
}

// WebSocket order update
interface WsOrderData {
  order_id: string;
  user_id: string;
  market: string;
  side: string;
  order_type: string;
  price: string;
  size: string;
  filled_size: string;
  status: string;
  take_profit?: string | null;
  stop_loss?: string | null;
  timestamp: number;
}

// WebSocket balance update
interface WsBalanceData {
  user_id: string;
  collateral: string;
  available_margin: string;
  used_margin: string;
  unrealized_pnl: string;
  margin_mode?: string; // Optional for backwards compatibility
  timestamp: number;
}

// WebSocket fill update (for real-time trade history)
interface WsFillData {
  id: string;
  user_id: string;
  order_id: string;
  market: string;
  side: string;
  size: string;
  price: string;
  fee: string;
  realized_pnl: string;
  fill_type: string;
  timestamp: number;
}

// WebSocket funding payment update
interface WsFundingData {
  id: string;
  user_id: string;
  market: string;
  position_size: string;
  funding_rate: string;
  payment: string;
  timestamp: number;
}

// Convert backend Position to store format
function toStorePosition(pos: Position, timestamp?: number): StorePosition {
  const size = parseDecimal(pos.size);
  return {
    id: `${pos.user_id}-${pos.market}`,
    market: pos.market,
    side: size >= 0 ? "long" : "short",
    size: Math.abs(size),
    entryPrice: parseDecimal(pos.entry_price),
    currentPrice: parseDecimal(pos.mark_price),
    leverage: parseDecimal(pos.leverage),
    liquidationPrice: pos.liquidation_price ? parseDecimal(pos.liquidation_price) : 0,
    unrealizedPnl: parseDecimal(pos.unrealized_pnl),
    realizedPnl: parseDecimal(pos.realized_pnl),
    margin: parseDecimal(pos.margin),
    status: "open",
    openedAt: new Date(pos.opened_at),
    updatedAt: new Date(pos.updated_at),
    takeProfit: pos.take_profit ? parseDecimal(pos.take_profit) : undefined,
    stopLoss: pos.stop_loss ? parseDecimal(pos.stop_loss) : undefined,
    timestamp: timestamp || Date.now(),
  };
}

// Convert backend Order to store format
function toStoreOrder(order: Order, timestamp?: number): StoreOrder {
  return {
    id: order.id,
    market: order.market,
    side: order.side,
    orderType: order.order_type,
    price: parseDecimal(order.price),
    size: parseDecimal(order.size),
    filledSize: parseDecimal(order.filled_size),
    status: order.status,
    takeProfit: order.take_profit ? parseDecimal(order.take_profit) : null,
    stopLoss: order.stop_loss ? parseDecimal(order.stop_loss) : null,
    createdAt: new Date(order.created_at),
    updatedAt: new Date(order.updated_at),
    timestamp: timestamp || Date.now(),
  };
}

// Convert WebSocket position to store format
function wsToStorePosition(ws: WsPositionData): StorePosition {
  const size = parseDecimal(ws.size);
  return {
    id: ws.id || `${ws.user_id}-${ws.market}`,
    market: ws.market,
    side: ws.side as "long" | "short",
    size: Math.abs(size),
    entryPrice: parseDecimal(ws.entry_price),
    currentPrice: parseDecimal(ws.mark_price),
    leverage: parseDecimal(ws.leverage),
    liquidationPrice: ws.liquidation_price ? parseDecimal(ws.liquidation_price) : 0,
    unrealizedPnl: parseDecimal(ws.unrealized_pnl),
    realizedPnl: parseDecimal(ws.realized_pnl),
    margin: parseDecimal(ws.margin),
    status: size === 0 ? "closed" : "open",
    openedAt: new Date(ws.opened_at),
    updatedAt: new Date(),
    takeProfit: ws.take_profit ? parseDecimal(ws.take_profit) : undefined,
    stopLoss: ws.stop_loss ? parseDecimal(ws.stop_loss) : undefined,
    timestamp: ws.timestamp,
  };
}

// Convert WebSocket order to store format
function wsToStoreOrder(ws: WsOrderData): StoreOrder {
  return {
    id: ws.order_id,
    market: ws.market,
    side: ws.side as "buy" | "sell",
    orderType: ws.order_type as "market" | "limit",
    price: parseDecimal(ws.price),
    size: parseDecimal(ws.size),
    filledSize: parseDecimal(ws.filled_size),
    status: ws.status,
    takeProfit: ws.take_profit ? parseDecimal(ws.take_profit) : null,
    stopLoss: ws.stop_loss ? parseDecimal(ws.stop_loss) : null,
    createdAt: new Date(),
    updatedAt: new Date(),
    timestamp: ws.timestamp,
  };
}

// Convert WebSocket fill to store format
function wsToStoreFill(ws: WsFillData): StoreFill {
  return {
    id: ws.id,
    orderId: ws.order_id,
    market: ws.market,
    side: ws.side as "buy" | "sell",
    size: parseDecimal(ws.size),
    price: parseDecimal(ws.price),
    fee: parseDecimal(ws.fee),
    realizedPnl: parseDecimal(ws.realized_pnl),
    fillType: ws.fill_type,
    createdAt: new Date(),
    timestamp: ws.timestamp,
  };
}

// Convert WebSocket funding payment to store format
function wsToStoreFundingPayment(ws: WsFundingData): StoreFundingPayment {
  return {
    id: ws.id,
    market: ws.market,
    positionSize: parseDecimal(ws.position_size),
    fundingRate: parseDecimal(ws.funding_rate),
    payment: parseDecimal(ws.payment),
    createdAt: new Date(),
    timestamp: ws.timestamp,
  };
}

// Initial state
const initialState = {
  positions: new Map<string, StorePosition>(),
  orders: new Map<string, StoreOrder>(),
  balance: null as StoreBalance | null,
  fills: new Map<string, StoreFill>(),
  fundingPayments: new Map<string, StoreFundingPayment>(),
  deletedPositions: new Map<string, number>(),
  lastHttpSync: 0,
  lastWsUpdate: 0,
  isInitialized: false,
  isLoading: false,
};

export const useAccountStore = create<AccountState>((set, get) => ({
  ...initialState,

  /**
   * Full sync from HTTP response.
   * Replaces all data and sets sync timestamp.
   * Called on initial load or manual refresh.
   * 
   * FIX: Also syncs position mark_prices to the price store to ensure
   * consistent unrealized PnL calculations on page load.
   */
  syncFromHttp: (data: HttpSyncData) => {
    const now = Date.now();
    
    // FIX: Collect price updates from positions to sync to price store
    const priceUpdates: Array<{ symbol: string; price: number }> = [];
    
    set((state) => {
      const newPositions = new Map<string, StorePosition>();
      const newOrders = new Map<string, StoreOrder>();
      let newBalance = state.balance;

      // Sync positions
      if (data.positions) {
        for (const pos of data.positions) {
          const size = parseDecimal(pos.size);
          // Only include open positions (non-zero size)
          if (size !== 0) {
            const storePos = toStorePosition(pos, now);
            newPositions.set(storePos.market, storePos);
            
            // FIX: Collect price for price store sync
            if (storePos.currentPrice > 0) {
              priceUpdates.push({ symbol: storePos.market, price: storePos.currentPrice });
            }
          }
        }
      }

      // Sync orders
      if (data.orders) {
        for (const order of data.orders) {
          const storeOrder = toStoreOrder(order, now);
          newOrders.set(storeOrder.id, storeOrder);
        }
      }

      // Sync balance
      if (data.account) {
        const acc = data.account.account;
        newBalance = {
          collateral: parseDecimal(acc.collateral),
          availableMargin: parseDecimal(acc.available_margin),
          usedMargin: parseDecimal(acc.used_margin),
          unrealizedPnl: parseDecimal(acc.total_unrealized_pnl),
          realizedPnl: parseDecimal(acc.total_realized_pnl),
          totalPositionValue: parseDecimal(data.account.total_position_value),
          accountLeverage: parseDecimal(data.account.account_leverage),
          marginRatio: parseDecimal(data.account.margin_ratio),
          marginMode: acc.margin_mode as "isolated" | "cross",
          timestamp: now,
        };
      }

      return {
        positions: data.positions ? newPositions : state.positions,
        orders: data.orders ? newOrders : state.orders,
        balance: newBalance,
        // Clear tombstones on HTTP sync - HTTP is source of truth
        deletedPositions: new Map<string, number>(),
        lastHttpSync: now,
        isInitialized: true,
        isLoading: false,
      };
    });
    
    // FIX: Sync prices to price store after position sync is complete
    // This ensures unrealized PnL calculations use correct current prices
    if (priceUpdates.length > 0) {
      usePriceStore.getState().setPrices(priceUpdates);
    }
  },

  /**
   * Apply mutation result immediately.
   * Called after HTTP mutations (place order, close position, etc.)
   * This is the source of truth for the action that was just performed.
   */
  applyMutationResult: (result: MutationResult) => {
    const now = Date.now();
    
    set((state) => {
      const updates: Partial<AccountState> = {};

      // Apply order update
      if (result.order) {
        const newOrders = new Map(state.orders);
        const storeOrder = toStoreOrder(result.order, now);
        
        // If order is filled/cancelled, remove it from open orders
        if (storeOrder.status === "filled" || storeOrder.status === "cancelled") {
          newOrders.delete(storeOrder.id);
        } else {
          newOrders.set(storeOrder.id, storeOrder);
        }
        
        updates.orders = newOrders;
      }

      // Apply position update
      if (result.position) {
        const newPositions = new Map(state.positions);
        const storePos = toStorePosition(result.position, now);
        
        // If position is closed (size = 0), remove it
        if (storePos.size === 0) {
          newPositions.delete(storePos.market);
        } else {
          newPositions.set(storePos.market, storePos);
        }
        
        updates.positions = newPositions;
      }

      // Apply balance update
      if (result.balance && state.balance) {
        updates.balance = {
          ...state.balance,
          collateral: parseDecimal(result.balance.collateral),
          availableMargin: parseDecimal(result.balance.available_margin),
          usedMargin: parseDecimal(result.balance.used_margin),
          timestamp: now,
        };
      }

      return updates;
    });
  },

  /**
   * Apply WebSocket position update.
   * Only updates if the incoming data is newer than existing.
   * Respects tombstones for optimistically deleted positions.
   * 
   * RACE CONDITION HANDLING:
   * - All timestamps are backend timestamps (prevents clock skew issues)
   * - HTTP mutations set lastHttpSync to position.updated_at (backend time)
   * - WebSocket updates are rejected if wsData.timestamp < lastHttpSync
   * - If WS arrives before HTTP: HTTP overwrites (HTTP is source of truth for mutations)
   * - If HTTP arrives before WS: WS is applied only if newer (T_ws > T_http)
   * 
   * This ensures:
   * 1. No stale data overwrites fresh data
   * 2. HTTP mutations are authoritative
   * 3. Subsequent WS updates keep data fresh
   */
  applyWsPositionUpdate: (wsData: WsPositionData) => {
    const state = get();
    const now = Date.now();
    
    // Ignore if older than last HTTP sync (both are backend timestamps now)
    if (wsData.timestamp < state.lastHttpSync) {
      return;
    }

    const storePos = wsToStorePosition(wsData);
    const existing = state.positions.get(storePos.market);
    const tombstoneTime = state.deletedPositions.get(storePos.market);

    // Check tombstone - if position was recently deleted, ignore stale updates
    if (tombstoneTime) {
      const tombstoneAge = now - tombstoneTime;
      
      // If the WS update confirms the position is closed (size = 0), clear the tombstone
      if (storePos.size === 0) {
        set((state) => {
          const newDeletedPositions = new Map(state.deletedPositions);
          newDeletedPositions.delete(storePos.market);
          return { deletedPositions: newDeletedPositions };
        });
        return; // Position is confirmed closed, nothing more to do
      }
      
      // If tombstone hasn't expired, ignore this update (prevents re-adding)
      if (tombstoneAge < TOMBSTONE_EXPIRY_MS) {
        return;
      }
      
      // Tombstone expired - clear it and allow the update
      // (This handles the case where the close failed and position still exists)
      set((state) => {
        const newDeletedPositions = new Map(state.deletedPositions);
        newDeletedPositions.delete(storePos.market);
        return { deletedPositions: newDeletedPositions };
      });
    }

    // Only update if newer than existing
    if (existing && existing.timestamp >= storePos.timestamp) {
      return;
    }

    // FIX: Update price store with position's mark_price to keep prices in sync
    // This ensures unrealized PnL calculations use consistent current prices
    if (storePos.size > 0 && storePos.currentPrice > 0) {
      usePriceStore.getState().setPrice(storePos.market, storePos.currentPrice);
    }

    set((state) => {
      const newPositions = new Map(state.positions);
      
      // Remove closed positions (size = 0)
      if (storePos.size === 0) {
        newPositions.delete(storePos.market);
      } else {
        newPositions.set(storePos.market, storePos);
      }
      
      // Recalculate balance-derived values when positions change
      // This ensures accountLeverage and totalPositionValue stay accurate
      let updatedBalance = state.balance;
      if (state.balance) {
        const equity = state.balance.collateral + state.balance.unrealizedPnl;
        
        // Calculate totalPositionValue from the updated positions
        const totalPositionValue = Array.from(newPositions.values())
          .filter(p => p.status === "open" && p.size > 0)
          .reduce((sum, p) => sum + (p.size * p.currentPrice), 0);
        
        // Calculate accountLeverage = totalPositionValue / equity
        const accountLeverage = equity > 0 && totalPositionValue > 0
          ? totalPositionValue / equity
          : 0;
        
        updatedBalance = {
          ...state.balance,
          totalPositionValue,
          accountLeverage,
        };
      }
      
      return {
        positions: newPositions,
        balance: updatedBalance,
        lastWsUpdate: wsData.timestamp,
      };
    });
  },

  /**
   * Apply WebSocket order update.
   * Only updates if the incoming data is newer than existing.
   */
  applyWsOrderUpdate: (wsData: WsOrderData) => {
    const state = get();
    
    // Ignore if older than last HTTP sync
    if (wsData.timestamp < state.lastHttpSync) {
      return;
    }

    const storeOrder = wsToStoreOrder(wsData);
    const existing = state.orders.get(storeOrder.id);

    // Only update if newer than existing
    if (existing && existing.timestamp >= storeOrder.timestamp) {
      return;
    }

    set((state) => {
      const newOrders = new Map(state.orders);
      
      // Remove filled/cancelled orders
      if (storeOrder.status === "filled" || storeOrder.status === "cancelled") {
        newOrders.delete(storeOrder.id);
      } else {
        newOrders.set(storeOrder.id, storeOrder);
      }
      
      return {
        orders: newOrders,
        lastWsUpdate: wsData.timestamp,
      };
    });
  },

  /**
   * Apply WebSocket balance update.
   * Only updates if the incoming data is newer than existing.
   * Recalculates derived values (marginRatio, accountLeverage) from raw data.
   */
  applyWsBalanceUpdate: (wsData: WsBalanceData) => {
    const state = get();
    
    // Ignore if older than last HTTP sync
    if (wsData.timestamp < state.lastHttpSync) {
      return;
    }

    // Ignore if we don't have existing balance (wait for HTTP init)
    if (!state.balance) {
      return;
    }

    // Only update if newer
    if (state.balance.timestamp >= wsData.timestamp) {
      return;
    }

    // Parse the incoming raw values
    const collateral = parseDecimal(wsData.collateral);
    const availableMargin = parseDecimal(wsData.available_margin);
    const usedMargin = parseDecimal(wsData.used_margin);
    const unrealizedPnl = parseDecimal(wsData.unrealized_pnl);
    
    // Calculate equity = collateral + unrealizedPnl
    const equity = collateral + unrealizedPnl;
    
    // Calculate marginRatio = (equity / usedMargin) * 100
    // This matches the backend formula in get_account endpoint
    const marginRatio = usedMargin > 0 
      ? (equity / usedMargin) * 100 
      : 0;
    
    // Calculate totalPositionValue from positions in the store
    // totalPositionValue = sum of (|size| * currentPrice) for all open positions
    const totalPositionValue = Array.from(state.positions.values())
      .filter(p => p.status === "open" && p.size > 0)
      .reduce((sum, p) => sum + (p.size * p.currentPrice), 0);
    
    // Calculate accountLeverage = totalPositionValue / equity
    const accountLeverage = equity > 0 && totalPositionValue > 0
      ? totalPositionValue / equity
      : 0;

    set((state) => ({
      balance: state.balance ? {
        ...state.balance,
        collateral,
        availableMargin,
        usedMargin,
        unrealizedPnl,
        // Recalculated derived values
        marginRatio,
        accountLeverage,
        totalPositionValue,
        // Preserve or update margin mode if provided
        marginMode: wsData.margin_mode ? (wsData.margin_mode as "isolated" | "cross") : state.balance.marginMode,
        timestamp: wsData.timestamp,
      } : null,
      lastWsUpdate: wsData.timestamp,
    }));
  },

  /**
   * Apply WebSocket fill update.
   * Adds new fills to the store for real-time trade history updates.
   * 
   * PERFORMANCE: Limits fills to prevent memory leaks (keeps last 500)
   */
  applyWsFillUpdate: (wsData: WsFillData) => {
    const state = get();
    
    // Ignore if older than last HTTP sync
    if (wsData.timestamp < state.lastHttpSync) {
      return;
    }

    const storeFill = wsToStoreFill(wsData);
    const existing = state.fills.get(storeFill.id);

    // Only update if newer than existing (or if it doesn't exist)
    if (existing && existing.timestamp >= storeFill.timestamp) {
      return;
    }

    set((state) => {
      const newFills = new Map(state.fills);
      newFills.set(storeFill.id, storeFill);
      
      // PERFORMANCE: Limit fills to prevent unbounded growth
      // Keep only the most recent MAX_FILLS entries
      if (newFills.size > MAX_FILLS) {
        // Sort by timestamp and keep newest
        const fillsArray = Array.from(newFills.entries());
        fillsArray.sort((a, b) => b[1].timestamp - a[1].timestamp);
        const limitedFills = new Map(fillsArray.slice(0, MAX_FILLS));
        
        return {
          fills: limitedFills,
          lastWsUpdate: wsData.timestamp,
        };
      }
      
      return {
        fills: newFills,
        lastWsUpdate: wsData.timestamp,
      };
    });
  },

  /**
   * Apply WebSocket funding payment update.
   * Adds new funding payments to the store for real-time funding history updates.
   * 
   * PERFORMANCE: Limits payments to prevent memory leaks (keeps last 200)
   */
  applyWsFundingUpdate: (wsData: WsFundingData) => {
    const state = get();
    
    // Ignore if older than last HTTP sync
    if (wsData.timestamp < state.lastHttpSync) {
      return;
    }

    const storePayment = wsToStoreFundingPayment(wsData);
    const existing = state.fundingPayments.get(storePayment.id);

    // Only update if newer than existing (or if it doesn't exist)
    if (existing && existing.timestamp >= storePayment.timestamp) {
      return;
    }

    set((state) => {
      const newPayments = new Map(state.fundingPayments);
      newPayments.set(storePayment.id, storePayment);
      
      // PERFORMANCE: Limit funding payments to prevent unbounded growth
      // Keep only the most recent MAX_FUNDING_PAYMENTS entries
      if (newPayments.size > MAX_FUNDING_PAYMENTS) {
        // Sort by timestamp and keep newest
        const paymentsArray = Array.from(newPayments.entries());
        paymentsArray.sort((a, b) => b[1].timestamp - a[1].timestamp);
        const limitedPayments = new Map(paymentsArray.slice(0, MAX_FUNDING_PAYMENTS));
        
        return {
          fundingPayments: limitedPayments,
          lastWsUpdate: wsData.timestamp,
        };
      }
      
      return {
        fundingPayments: newPayments,
        lastWsUpdate: wsData.timestamp,
      };
    });
  },

  /**
   * Optimistically remove a position (for close position).
   * This provides instant UI feedback - the position disappears immediately.
   * Records a tombstone to prevent stale WebSocket updates from re-adding it.
   * If the API call fails, the tombstone will expire and WS updates will restore state.
   */
  removePosition: (market: string) => {
    const now = Date.now();
    set((state) => {
      const newPositions = new Map(state.positions);
      const newDeletedPositions = new Map(state.deletedPositions);
      
      // Remove the position
      newPositions.delete(market);
      
      // Record tombstone with current timestamp
      // This prevents stale WS updates from re-adding the position
      newDeletedPositions.set(market, now);
      
      return { 
        positions: newPositions,
        deletedPositions: newDeletedPositions,
      };
    });
  },

  /**
   * Update a single position from HTTP response.
   * Used after mutations (order placement, leverage change) to immediately
   * sync the position to the store without waiting for WebSocket.
   * This follows the industry-standard pattern: HTTP is source of truth for mutations.
   * 
   * CRITICAL: Uses backend timestamp (position.updated_at) to prevent clock skew issues.
   * Frontend and backend clocks may differ - always use backend time for comparisons.
   * 
   * FIX: Also updates the price store with the position's mark_price to ensure
   * unrealized PnL calculations use the correct current price immediately.
   * This prevents stale prices from causing incorrect initial PnL display.
   */
  updatePositionFromHttp: (position: Position) => {
    // Use backend timestamp from position.updated_at (milliseconds since epoch)
    // This prevents clock skew between frontend and backend
    const backendTimestamp = new Date(position.updated_at).getTime();
    const storePos = toStorePosition(position, backendTimestamp);
    
    // FIX: Update price store with position's mark_price to ensure PnL calculation
    // uses the correct current price immediately after position is created/updated.
    // This prevents the race condition where price store has stale data.
    if (storePos.size > 0 && storePos.currentPrice > 0) {
      usePriceStore.getState().setPrice(storePos.market, storePos.currentPrice);
    }
    
    set((state) => {
      const newPositions = new Map(state.positions);
      
      // If position is closed (size = 0), remove it
      if (storePos.size === 0) {
        newPositions.delete(storePos.market);
      } else {
        newPositions.set(storePos.market, storePos);
      }
      
      // Also clear any tombstone for this market since we have fresh data
      const newDeletedPositions = new Map(state.deletedPositions);
      newDeletedPositions.delete(storePos.market);
      
      // Recalculate balance-derived values when positions change
      let updatedBalance = state.balance;
      if (state.balance) {
        const equity = state.balance.collateral + state.balance.unrealizedPnl;
        
        // Calculate totalPositionValue from the updated positions
        const totalPositionValue = Array.from(newPositions.values())
          .filter(p => p.status === "open" && p.size > 0)
          .reduce((sum, p) => sum + (p.size * p.currentPrice), 0);
        
        // Calculate accountLeverage = totalPositionValue / equity
        const accountLeverage = equity > 0 && totalPositionValue > 0
          ? totalPositionValue / equity
          : 0;
        
        updatedBalance = {
          ...state.balance,
          totalPositionValue,
          accountLeverage,
        };
      }
      
      return { 
        positions: newPositions,
        deletedPositions: newDeletedPositions,
        balance: updatedBalance,
        lastHttpSync: backendTimestamp, // Use backend timestamp, not Date.now()
      };
    });
  },

  setLoading: (loading: boolean) => set({ isLoading: loading }),

  reset: () => set(initialState),
}));

// Export types
export type { HttpSyncData, MutationResult, WsPositionData, WsOrderData, WsBalanceData, WsFillData, WsFundingData };

/**
 * Selector for position TP/SL values.
 * Similar to usePrice() - allows cells to subscribe directly to store.
 * When TP/SL updates, only the TpSlCell for that market re-renders.
 * 
 * This is the same pattern used by price cells for real-time updates:
 * - Each cell subscribes to ONE position's TP/SL
 * - When BTC TP/SL changes, only BTC's TpSlCell re-renders
 * - Other cells are completely unaffected
 */
export const usePositionTpSl = (market: string): { takeProfit?: number; stopLoss?: number } => {
  const takeProfit = useAccountStore((state) => state.positions.get(market)?.takeProfit);
  const stopLoss = useAccountStore((state) => state.positions.get(market)?.stopLoss);
  return { takeProfit, stopLoss };
};

/**
 * Selector for position leverage value.
 * Similar to usePrice() and usePositionTpSl() - allows cells to subscribe directly to store.
 * When leverage updates, only the LeverageCell for that market re-renders.
 * 
 * This is the same pattern used by price cells and TP/SL cells for real-time updates:
 * - Each cell subscribes to ONE position's leverage
 * - When BTC leverage changes, only BTC's LeverageCell re-renders
 * - Other cells are completely unaffected
 */
export const usePositionLeverage = (market: string): number => {
  return useAccountStore((state) => state.positions.get(market)?.leverage ?? 1);
};

/**
 * Selector for account margin mode.
 * Returns the current margin mode (isolated or cross) or null if not initialized.
 */
export const useMarginMode = (): "isolated" | "cross" | null => {
  return useAccountStore((state) => state.balance?.marginMode ?? null);
};

/**
 * Selector for real-time unrealized PnL across all positions.
 * Follows the same pattern as usePrice() and usePositionTpSl().
 * 
 * Calculates using current market prices for positions that have them,
 * falls back to backend-calculated PnL otherwise.
 * 
 * This is a derived value - computed from positions + prices.
 * All components subscribe to the same calculation (single source of truth).
 * 
 * Industry standard: Real-time PnL is a derived state that belongs in the store,
 * not duplicated across components. Used by Binance, Bybit, HyperLiquid.
 * 
 * Note: This selector subscribes to both account and price stores.
 * Components will re-render when either positions or prices change.
 * Zustand handles memoization internally - no need for useMemo wrapper.
 */
export const useRealTimeUnrealizedPnL = (): number => {
  const positions = useAccountStore((state) => state.positions);
  const marketPrices = usePriceStore((state) => state.marketPrices);
  
  let total = 0;
  for (const [_market, position] of positions) {
    const currentPrice = marketPrices[position.market];
    if (currentPrice && currentPrice > 0) {
      // Calculate PnL: (currentPrice - entryPrice) * size for long
      //                (entryPrice - currentPrice) * size for short
      const priceDiff = position.side === "long"
        ? currentPrice - position.entryPrice
        : position.entryPrice - currentPrice;
      total += priceDiff * position.size;
    } else {
      // Fallback to backend-calculated PnL when price unavailable
      total += position.unrealizedPnl;
    }
  }
  return total;
};

